"use client"

import { useState } from "react"
import { VideoOff, Video } from "lucide-react"

interface VideoToggleButtonProps {
    showVideo?: boolean
    onToggle?: (isOn: boolean) => void
    className?: string
    size?: "sm" | "default" | "lg"
    disabled?: boolean
}

export function VideoToggleButton({
    showVideo: controlledShowVideo,
    onToggle,
    className = "",
    size = "default",
    disabled = false,
}: VideoToggleButtonProps) {
    const [internalShowVideo, setInternalShowVideo] = useState(false)

    // Use controlled state if provided, otherwise use internal state
    const showVideo = controlledShowVideo !== undefined ? controlledShowVideo : internalShowVideo

    const handleToggle = () => {
        if (disabled) return

        const newState = !showVideo

        // Update internal state if not controlled
        if (controlledShowVideo === undefined) {
            setInternalShowVideo(newState)
        }

        // Call external handler if provided
        onToggle?.(newState)
    }

    const sizeClasses = {
        sm: "px-2 py-1 text-xs gap-1",
        default: "px-2 py-1.5 text-sm gap-2",
        lg: "px-4 py-3 text-base gap-3",
    }

    const baseClasses = `
    inline-flex items-center justify-center rounded-xl font-medium
    transition-all duration-200 ease-in-out
    focus:outline-none 
    disabled:opacity-50 disabled:cursor-not-allowed
    shadow-sm hover:shadow-md active:scale-95
    border backdrop-blur-sm
  `

    const colorClasses = showVideo
        ? `bg-emerald-100/90 text-emerald-800 border-emerald-200/50 
       hover:bg-emerald-200/90 hover:border-emerald-300/50
       focus:ring-emerald-500/30
       dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800/50
       dark:hover:bg-emerald-900/80 dark:hover:border-emerald-700/50`
        : `bg-red-100/90 text-red-800 border-red-200/50
       hover:bg-red-200/90 hover:border-red-300/50  
       focus:ring-red-500/30
       dark:bg-red-950/80 dark:text-red-300 dark:border-red-800/50
       dark:hover:bg-red-900/80 dark:hover:border-red-700/50`

    return (
        <button
            onClick={handleToggle}
            disabled={disabled}
            className={`${baseClasses} ${colorClasses} ${sizeClasses[size]} ${className}`}
            aria-label={showVideo ? "Turn off video" : "Turn on video"}
            aria-pressed={showVideo}
            title={showVideo ? "Turn off video" : "Turn on video"}
            type="button"
        >
            <div className="flex items-center gap-inherit">
                <div className="transition-transform duration-200 ease-in-out">
                    {showVideo ? (
                        <Video className={`${size === "sm" ? "h-3 w-3" : size === "lg" ? "h-5 w-5" : "h-4 w-4"}`} />
                    ) : (
                        <VideoOff className={`${size === "sm" ? "h-3 w-3" : size === "lg" ? "h-5 w-5" : "h-4 w-4"}`} />
                    )}
                </div>

                <span className="font-medium ml-1">{showVideo ? "Turn off" : "Turn on"}</span>
            </div>

            <div
                className={`absolute -top-1 -right-1 h-2 w-2 rounded-full transition-all duration-200 ${showVideo
                    ? "bg-emerald-500 shadow-emerald-500/50  dark:bg-emerald-400"
                    : "bg-red-500 shadow-red-500/50 dark:bg-red-400"
                    }`}
                aria-hidden="true"
            />
        </button>
    )
}
